/**
 * Macro #26 · Increment 2 — LAZY loader for the PUBLIC voxel scene (NO three).
 *
 * `three` / R3F / drei live ONLY inside the dynamically imported VoxelScene
 * chunk. This wrapper probes WebGL FIRST (never importing three when the GPU
 * path is unavailable) and wraps the lazy scene in an error boundary + Suspense
 * so a chunk-load / WebGL failure degrades gracefully to the 2D fallback.
 */
import { lazy, Suspense, useMemo, type ReactNode } from "react";
import { WebGLBoundary } from "./WebGLBoundary";
import { isWebGLAvailable } from "./webgl";
import type { WorkArt } from "../domain/voxel3d";

const VoxelScene = lazy(() => import("./VoxelScene"));

export function LazyVoxelScene({
  workArts,
  sceneOpacity = 1,
  resetSignal = 0,
  fitSignal = 0,
  transparent = false,
  artSnapshot = null,
  artFootprint = null,
  fallback,
  loading,
  onError,
}: {
  workArts: WorkArt[];
  sceneOpacity?: number;
  resetSignal?: number;
  fitSignal?: number;
  transparent?: boolean;
  artSnapshot?: import("../lib/publication-api").PublicSnapshot | null;
  artFootprint?: import("./canvasArtFloor").FloorRect | null;
  fallback: ReactNode;
  loading: ReactNode;
  onError?: () => void;
}) {
  const ok = useMemo(() => isWebGLAvailable(), []);
  if (!ok) return <>{fallback}</>;
  return (
    <WebGLBoundary fallback={fallback} onError={onError}>
      <Suspense fallback={loading}>
        <VoxelScene workArts={workArts} sceneOpacity={sceneOpacity} resetSignal={resetSignal} fitSignal={fitSignal} transparent={transparent} artSnapshot={artSnapshot} artFootprint={artFootprint} />
      </Suspense>
    </WebGLBoundary>
  );
}
